import type { inferRouterOutputs } from '@trpc/server'

import type { AppRouter } from '@orus.eu/backend'
import { failure, type Result, success } from '@orus.eu/result'

import type { StandaloneFormAccess, StandaloneFormRedirect } from '../../standalone-form-access.types.ts'
import type { StandaloneFormPageState, StandaloneFormSubmitProblemType } from '../../standalone-form-entry.types.ts'

type NlAovqlHealthFormOutputs = inferRouterOutputs<AppRouter>['nlAovqlHealthForm']

export type NlAovqlHealthContext = NlAovqlHealthFormOutputs['getCurrentState']

type NlAovqlHealthFormAccess = NlAovqlHealthFormOutputs['getFormAccess']

type NlAovqlHealthSubmitOutput = NlAovqlHealthFormOutputs['submit']

type NlAovqlHealthSubmitProblemType = Extract<NlAovqlHealthSubmitOutput, { type: 'failure' }>['problem']['type']

export type NlAovqlHealthFormResult = 'under-review'

/**
 * Final once under review, so the page never shows a question of the submitted form. A verdict sends the customer back
 * to the gate step, which shows it.
 */
export function nlAovqlHealthPageState(
  context: NlAovqlHealthContext,
): StandaloneFormPageState<NlAovqlHealthFormResult> {
  switch (context.status) {
    case 'required':
      return { state: 'open' }
    case 'under-review':
      return { state: 'final', result: 'under-review' }
    case 'reviewed':
      return { state: 'redirect', redirect: NL_AOVQL_HEALTH_DENIED_REDIRECT['already-submitted'] }
  }
}

/** The page drops a draft started before the submission. */
export function nlAovqlHealthLastSubmissionId(context: NlAovqlHealthContext): string | undefined {
  switch (context.status) {
    case 'required':
      return undefined
    case 'under-review':
    case 'reviewed':
      return context.submissionId
  }
}

export function nlAovqlHealthSubmitResult(
  output: NlAovqlHealthSubmitOutput,
): Result<NlAovqlHealthFormResult, StandaloneFormSubmitProblemType> {
  switch (output.type) {
    case 'success':
      return success('under-review')
    case 'failure':
      return failure(NL_AOVQL_HEALTH_SUBMIT_PROBLEM[output.problem.type])
  }
}

const NL_AOVQL_HEALTH_SUBMIT_PROBLEM: Record<NlAovqlHealthSubmitProblemType, StandaloneFormSubmitProblemType> = {
  'gate-step-not-reached': 'form-not-available',
  'quote-already-signed': 'form-not-available',
  'knowledge-test-not-validated': 'form-not-available',
  'outdated-definition-version': 'outdated-definition-version',
  'submission-not-allowed': 'submission-not-allowed',
  'invalid-answers': 'invalid-answers',
  'sedgwick-send-failed': 'submission-failed',
}

export function nlAovqlHealthAccess(access: NlAovqlHealthFormAccess): StandaloneFormAccess {
  switch (access.type) {
    case 'allowed':
      return access
    case 'denied':
      return { type: 'not-available', redirect: NL_AOVQL_HEALTH_DENIED_REDIRECT[access.reason] }
  }
}

const NL_AOVQL_HEALTH_DENIED_REDIRECT: Record<
  Extract<NlAovqlHealthFormAccess, { type: 'denied' }>['reason'],
  StandaloneFormRedirect
> = {
  // The funnel moves a customer on from its quote step to the step they are at
  'gate-step-not-reached': { to: 'funnel', stepId: 'quote' },
  'quote-already-signed': { to: 'customer-portal' },
  'knowledge-test-not-validated': { to: 'funnel', stepId: 'nl-aovql-forms-gate' },
  // Submitted: the gate step shows its status
  'already-submitted': { to: 'funnel', stepId: 'nl-aovql-forms-gate' },
}
